import React, { useCallback, useEffect, useState } from "react";
import {
    Loader2, Plus, Edit2, Trash2, GitMerge, Upload, Tag,
    RefreshCw, Check, X, FileText, AlertTriangle,
} from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

/**
 * iter-168 — Admin management for the shared skills taxonomy. Used by both
 * job postings (required_skills) and talent passports (skills). Matching
 * runs on taxonomy overlap, so adds/renames/merges here directly affect
 * what employers can match on and what passports can declare.
 */
const AdminSkills = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [cat, setCat] = useState("all");
    const [newOpen, setNewOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [merging, setMerging] = useState(null);
    const [importing, setImporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/skills");
            setRows(data.skills || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const categories = Array.from(new Set(rows.map((r) => r.category))).sort();
    const visible = rows.filter((r) => (
        (cat === "all" || r.category === cat) &&
        (r.label.toLowerCase().includes(query.toLowerCase()) ||
         (r.category || "").toLowerCase().includes(query.toLowerCase()))
    ));

    const remove = async (r) => {
        if (!window.confirm(`Delete '${r.label}'? Blocked if any active job requires it.`)) return;
        try { await api.delete(`/admin/skills/${r.id}`); toast.success("Deleted."); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const importCsv = async (file) => {
        if (!file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/skills/import", fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Imported ${data.created} skill${data.created === 1 ? "" : "s"}${data.skipped.length ? ` (${data.skipped.length} skipped)` : ""}.`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setImporting(false); }
    };

    return (
        <div data-testid="admin-skills-page">
            <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                        <Tag size={16} /> <p className="overline">Taxonomy</p>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-foreground mt-1">Skills</h1>
                    <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
                        One list, used by both Talent Passports and Role postings. Match-browse runs on taxonomy overlap — renames and merges propagate everywhere.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1 cursor-pointer" data-testid="skills-import-trigger">
                        {importing ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Import CSV
                        <input type="file" accept=".csv" className="hidden"
                               data-testid="skills-import-input"
                               onChange={(e) => { const f = e.target.files?.[0]; if (f) importCsv(f); e.target.value = ""; }} />
                    </label>
                    <button onClick={load} data-testid="skills-refresh" className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1">
                        <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                    <button onClick={() => setNewOpen(true)} data-testid="skills-new" className="btn-green text-xs px-3 py-1.5 inline-flex items-center gap-1">
                        <Plus size={11} /> New skill
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Input placeholder="Search" value={query} onChange={(e) => setQuery(e.target.value)}
                       data-testid="skills-search" className="h-8 text-sm w-60" />
                <Select value={cat} onValueChange={setCat}>
                    <SelectTrigger data-testid="skills-category-filter" className="h-8 w-48 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All categories</SelectItem>
                        {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                </Select>
                <span className="text-[11px] text-muted-foreground">{visible.length} of {rows.length}</span>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-emerald-500" /></div>
            ) : visible.length === 0 ? (
                <div className="card-soft p-10 text-center text-sm text-muted-foreground" data-testid="skills-empty">
                    No skills match. Try clearing the filter or import a CSV.
                </div>
            ) : (
                <div className="card-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="text-left px-3 py-2 font-semibold">Label</th>
                                <th className="text-left px-3 py-2 font-semibold">Category</th>
                                <th className="text-right px-3 py-2 font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {visible.map((r) => (
                                <tr key={r.id} data-testid={`skill-row-${r.id}`} className="hover:bg-muted/20">
                                    <td className="px-3 py-2 text-foreground font-medium">{r.label}</td>
                                    <td className="px-3 py-2 text-muted-foreground">{r.category}</td>
                                    <td className="px-3 py-2 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <button onClick={() => setEditing(r)} data-testid={`skill-edit-${r.id}`}
                                                className="p-1 rounded hover:bg-muted/60" title="Rename">
                                                <Edit2 size={11} />
                                            </button>
                                            <button onClick={() => setMerging(r)} data-testid={`skill-merge-${r.id}`}
                                                className="p-1 rounded hover:bg-muted/60" title="Merge">
                                                <GitMerge size={11} />
                                            </button>
                                            <button onClick={() => remove(r)} data-testid={`skill-delete-${r.id}`}
                                                className="p-1 rounded hover:bg-rose-500/15 text-rose-600 dark:text-rose-400" title="Delete">
                                                <Trash2 size={11} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <SkillFormDialog
                open={newOpen || !!editing}
                skill={editing}
                onClose={() => { setNewOpen(false); setEditing(null); }}
                onSaved={() => { setNewOpen(false); setEditing(null); load(); }}
            />
            <MergeDialog
                skill={merging}
                options={rows}
                onClose={() => setMerging(null)}
                onMerged={() => { setMerging(null); load(); }}
            />
        </div>
    );
};

const SkillFormDialog = ({ open, skill, onClose, onSaved }) => {
    const [label, setLabel] = useState("");
    const [category, setCategory] = useState("General");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setLabel(skill?.label || ""); setCategory(skill?.category || "General");
    }, [skill, open]);

    const save = async () => {
        setSaving(true);
        try {
            if (skill?.id) {
                await api.patch(`/admin/skills/${skill.id}`, { label, category });
                toast.success("Skill updated.");
            } else {
                await api.post("/admin/skills", { label, category });
                toast.success("Skill created.");
            }
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="skill-form-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{skill?.id ? "Edit skill" : "New skill"}</DialogTitle>
                    <DialogDescription>
                        Labels are case-insensitive unique across the taxonomy. Renames propagate everywhere the label is referenced.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <div>
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Label</label>
                        <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80}
                               data-testid="skill-form-label" className="mt-1 h-9 text-sm" placeholder="e.g. Phlebotomy" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category</label>
                        <Input value={category} onChange={(e) => setCategory(e.target.value)} maxLength={40}
                               data-testid="skill-form-category" className="mt-1 h-9 text-sm" placeholder="General" />
                    </div>
                </div>
                <DialogFooter>
                    <button onClick={onClose} data-testid="skill-form-cancel" className="btn-outline text-sm px-4 py-2 inline-flex items-center gap-1"><X size={13} /> Cancel</button>
                    <button onClick={save} disabled={saving || !label.trim()} data-testid="skill-form-save" className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60">
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const MergeDialog = ({ skill, options, onClose, onMerged }) => {
    const [into, setInto] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => { setInto(""); }, [skill]);

    if (!skill) return <Dialog open={false} onOpenChange={() => {}}><DialogContent /></Dialog>;

    const otherOptions = options.filter((o) => o.id !== skill.id);

    const run = async () => {
        if (!into) return;
        setSaving(true);
        try {
            const { data } = await api.post(`/admin/skills/${skill.id}/merge`, { into_skill_id: into });
            toast.success(`Merged. ${data.jobs_relabeled} job(s) + ${data.passports_relabeled} passport(s) relabeled.`);
            onMerged();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!skill} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="skill-merge-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><GitMerge size={16} className="text-emerald-500" /> Merge &quot;{skill.label}&quot;</DialogTitle>
                    <DialogDescription>
                        Every job + passport using &quot;{skill.label}&quot; will be relabeled to the target skill, then this row is deleted. Idempotent and audited.
                    </DialogDescription>
                </DialogHeader>
                <div className="py-2">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Merge into</label>
                    <Select value={into} onValueChange={setInto}>
                        <SelectTrigger data-testid="skill-merge-into" className="h-9 mt-1 text-sm"><SelectValue placeholder="Pick a target skill" /></SelectTrigger>
                        <SelectContent>
                            {otherOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="text-[11px] text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                    <AlertTriangle size={11} /> This cannot be undone from the UI (admin DB fix only).
                </div>
                <DialogFooter>
                    <button onClick={onClose} data-testid="skill-merge-cancel" className="btn-outline text-sm px-4 py-2">Cancel</button>
                    <button onClick={run} disabled={saving || !into} data-testid="skill-merge-submit" className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60">
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <GitMerge size={13} />} Merge
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default AdminSkills;
